import { Injectable } from '@angular/core';
import { RememberService } from '@app/core/services/remember.service';

export interface StateModalStoredSize {
    width?: number;
    height?: number;
    x?: number;
    y?: number;
    useDefault?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class StateModalSizeService {
    constructor(private remember: RememberService) {}

    public async getSize(state: string): Promise<StateModalStoredSize | undefined> {
        try {
            const stored = (await this.remember.getLastStatus(this.getRememberId(state), true)) as StateModalStoredSize | null;

            const position = this.getValidPosition(stored);

            if (stored?.useDefault === true) {
                return { useDefault: true, ...position };
            }

            if (stored && this.isValidDimension(stored.width) && this.isValidDimension(stored.height)) {
                return { width: stored.width, height: stored.height, ...position };
            }
        } catch (error) {
            console.error(error);
        }

        return undefined;
    }

    public rememberSize(state: string, width: number, height: number, position?: StateModalPosition): Promise<void> {
        if (!this.isValidDimension(width) || !this.isValidDimension(height)) {
            return Promise.resolve();
        }

        return this.remember.remember(this.getRememberId(state), { width, height, ...this.getValidPosition(position) });
    }

    public rememberDefault(state: string, position?: StateModalPosition): Promise<void> {
        return this.remember.remember(this.getRememberId(state), { useDefault: true, ...this.getValidPosition(position) });
    }

    public getRememberId(state: string): string {
        return `state-modal.size.${state}.${screen.width}.${screen.height}`;
    }

    private isValidDimension(value: unknown): value is number {
        return typeof value === 'number' && Number.isFinite(value) && value > 0;
    }

    private getValidPosition(position: Partial<StateModalPosition> | null | undefined): StateModalPosition | undefined {
        const x = position?.x;
        const y = position?.y;

        if (this.isValidCoordinate(x) && this.isValidCoordinate(y)) {
            return { x, y };
        }

        return undefined;
    }

    private isValidCoordinate(value: unknown): value is number {
        return typeof value === 'number' && Number.isFinite(value) && value >= 0;
    }
}

export interface StateModalPosition {
    x: number;
    y: number;
}
